import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { api, Notification, NotificationListResponse } from "@/services/api";

export const NOTIFICATIONS_QUERY_KEY = ["notifications", "list"] as const;
export const NOTIFICATIONS_UNREAD_QUERY_KEY = ["notifications", "unread-count"] as const;

// Only refetch the badge count — used after auto-read and single-item reads
// so the list itself doesn't flicker or disappear while the panel is open.
const invalidateBadge = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_QUERY_KEY });
};

// Full invalidation — used only for Mark All Read where every row changes.
const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_QUERY_KEY });
};

// Optimistically flip a set of notification IDs to isRead=true in the list cache
// so the blue dots disappear instantly without a network round-trip.
const optimisticMarkRead = (
  queryClient: ReturnType<typeof useQueryClient>,
  ids: Set<number>,
) => {
  queryClient.setQueriesData<{ pages: NotificationListResponse[] }>(
    { queryKey: NOTIFICATIONS_QUERY_KEY },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          unreadCount: page.unreadCount - page.items.filter((n) => ids.has(n.id) && !n.isRead).length,
          items: page.items.map((n) =>
            ids.has(n.id) ? { ...n, isRead: true } : n,
          ),
        })),
      };
    },
  );
};

export const useInfiniteNotifications = (isOpen: boolean) => {
  return useInfiniteQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: ({ pageParam = 1 }) => api.notifications.list(pageParam as number, 10),
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.pages) return undefined;
      return lastPage.page + 1;
    },
    enabled: isOpen,
    initialPageParam: 1,
  });
};

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_QUERY_KEY,
    queryFn: api.notifications.unreadCount,
    refetchInterval: 60_000,
  });
};

export const useMarkAllRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.notifications.markAllRead,
    // Mark all optimistically then do a full refetch so counts + dots are both correct
    onMutate: () => {
      queryClient.setQueriesData<{ pages: NotificationListResponse[] }>(
        { queryKey: NOTIFICATIONS_QUERY_KEY },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              unreadCount: 0,
              items: page.items.map((n) => ({ ...n, isRead: true })),
            })),
          };
        },
      );
    },
    onSuccess: () => invalidateAll(queryClient),
  });
};

export const useMarkIdsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationIds: number[]) => api.notifications.markIdsRead(notificationIds),
    // Optimistic: flip dots immediately; only refresh the badge count on success
    onMutate: (ids) => {
      optimisticMarkRead(queryClient, new Set(ids));
    },
    onSuccess: () => invalidateBadge(queryClient),
  });
};

export const useMarkOneRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: number) => api.notifications.markOneRead(notificationId),
    // Optimistic: flip the single dot immediately; only refresh the badge count on success
    onMutate: (id) => {
      optimisticMarkRead(queryClient, new Set([id]));
    },
    onSuccess: () => invalidateBadge(queryClient),
  });
};

export const useUnreadMessageCount = () => {
  // Scoped to the active workspace so the badge only counts conversations the
  // messages list shows — a dual-role user otherwise saw unread messages from
  // their other workspace with no row to open. Invalidating
  // ["messages", "unread-count"] still matches this key by prefix.
  const workspace = useActiveWorkspace();
  return useQuery({
    queryKey: ["messages", "unread-count", workspace],
    queryFn: () => api.messages.unreadCount(workspace),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
};
